<template>
  <DialogBackdrop v-if="show" :class="['dialog__backdrop', { padding }]" @mousedown.native="handleBackdropClick">
    <!-- Wrapper -->
    <div class="dialog__wrapper">
      <Animation :duration="350" :enter="{ xs: 'slideInUp', md: 'zoomIn' }" appear>
        <!-- Dialog -->
        <div :class="['dialog', contentClass, { large, maximized, spaced }]" :style="contentStyle" @mousedown.stop>
          <!-- Header -->
          <DialogHeader class="dialog__header">
            <!-- Close button -->
            <IconButton v-if="back" name="arrow-left" :size="18" @click.native="onDialogCancel" />

            <!-- Title -->
            <template v-if="!!title">
              <h1 class="dialog__header__title" :class="[ titleSize ]">{{ title }}</h1>
            </template>

            <!-- Close button -->
            <IconButton v-if="closable" name="close" :size="18" @click.native="show = false" />
          </DialogHeader>

          <!-- Body -->
          <DialogBody class="dialog__body">
            <ScrollArea :shadow="scrollShadow" class="dialog__body__scroll">
              <div class="dialog__body__content">
                <slot />
              </div>
            </ScrollArea>
          </DialogBody>

          <!-- Footer -->
          <DialogFooter v-if="footer" class="dialog__footer" :class="{ 'dialog__footer--sticky': footerSticky }">
            <slot name="footer">
              <IconButton v-if="footerClose && !back" btn-style="secondary" name="close" btn-size="sm" :size="14" type="stroke" :color="NEUTRAL_SUPERDARK" circular @click.native="show = false"></IconButton>
              <IconButton v-else-if="footerClose" btn-style="secondary" name="arrow-left" btn-size="sm" :size="14" type="solid" :color="NEUTRAL_SUPERDARK" circular @click.native="onDialogCancel"></IconButton>
              <Button v-else btn-style="primary" @click.native="onDialogOk">Aceptar</Button>
            </slot>
          </DialogFooter>
        </div>
      </Animation>
    </div>
  </DialogBackdrop>
</template>

<style lang="postcss" scoped>
.dialog {
  @apply absolute md:static flex flex-col bg-neutral-white md:rounded-lg shadow-xl overflow-hidden;
  @apply max-w-none md:max-w-md max-h-full min-h-[12rem];
  @apply top-0 bottom-0 left-0 right-0 md:top-auto md:bottom-auto md:left-auto md:right-auto;

  &.large {
    @apply max-w-3xl;
  }

  &.spaced {
    @apply md:min-h-[14rem];
  }

  &.maximized {
    @apply absolute top-0 bottom-0 left-0 right-0 max-w-none;
    @apply rounded-none;
  }

  &__backdrop {
    @apply z-30;
    &.padding {
      @apply md:p-sm;
    }
  }

  &__wrapper {
    @apply relative w-full h-full;
    @apply flex flex-col justify-center items-center;
  }

  &__header,
  &__footer {
    @apply grow px-xxs md:px-xxs flex flex-row justify-between items-center;

    .spaced & {
      @apply md:px-sm;
    }
  }

  &__header {
    @apply md:pt-xxs md:pb-xxxs flex flex-row justify-between items-center;

    .spaced & {
      @apply pt-xxs pb-xxxs md:pt-sm md:pb-xxs;
    }

    &__title {
      @apply style-subtitle md:style-title-xs m-0;

      &.sm {
        @apply style-subtitle-sm-bold;
      }
    }
  }

  &__footer {
    @apply md:pb-xxs md:pt-xxxs;

    &--sticky {
      @apply sticky bottom-0 p-xxxs flex-none;
      box-shadow: 0px -2px 2px 0px rgba(0, 0, 0, 0.15);
    }

    .spaced & {
      @apply pb-xxs pt-xxxs md:pb-sm md:pt-xxs;
    }
  }

  &__body {
    @apply grow overflow-hidden;

    &__scroll {
      @apply w-full h-full;
    }

    &__content {
      @apply px-xxs;

      .spaced & {
        @apply md:px-sm;
      }
    }
  }
}
</style>

<script>
import { ref, toRefs, watch } from '@vue/composition-api';
import { getColor } from '@/lego/utils/tailwind/config';
import DialogBackdrop from '@/lego/components/dialogs/DialogBackdrop.vue';
import DialogHeader from '@/lego/components/dialogs/DialogHeader.vue';
import DialogBody from '@/lego/components/dialogs/DialogBody.vue';
import DialogFooter from '@/lego/components/dialogs/DialogFooter.vue';
import Animation from '@/lego/components/tools/Animation.vue';
import IconButton from '@/lego/components/buttons/IconButton.vue';
import Button from '@/lego/components/buttons/Button.vue';
import ScrollArea from '@/lego/components/layout/ScrollArea.vue';

const NEUTRAL_SUPERDARK = getColor('neutral.superdark');

export default {
  name: 'DialogComponent',

  components: {
    DialogBackdrop,
    DialogHeader,
    DialogBody,
    DialogFooter,
    ScrollArea,
    Animation,
    IconButton,
    Button,
  },

  props: {
    value: {
      type: Boolean,
      default: true,
    },

    large: {
      type: Boolean,
      default: false,
    },

    maximized: {
      type: Boolean,
      default: false,
    },

    spaced: {
      type: Boolean,
      default: false,
    },

    padding: {
      type: Boolean,
      default: false,
    },

    persistent: {
      type: Boolean,
      default: false,
    },

    closable: {
      type: Boolean,
      default: true,
    },

    title: {
      type: String,
      required: false,
    },

    titleSize: {
      type: String,
      required: false,
    },

    scrollShadow: {
      type: Boolean,
      default: false,
    },

    contentClass: {
      type: [String, Array],
      required: false,
    },

    contentStyle: {
      type: [String, Object],
      required: false,
    },

    footer: {
      type: Boolean,
      default: true,
    },

    back: {
      type: Boolean,
      default: false,
    },

    footerSticky: {
      type: Boolean,
      default: false,
    },

    footerClose: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const { value, persistent } = toRefs(props);
    const show = ref(value.value);

    function handleBackdropClick() {
      if (!persistent.value) {
        show.value = false;
      }
    }

    watch(value, val => (show.value = val));

    watch(show, val => {
      if (!val) {
        onDialogDismiss();
      }

      emit('input', val);
    });

    const onDialogOk = evt => {
      emit('ok', evt);
      show.value = false;
    };

    const onDialogCancel = evt => {
      emit('cancel', evt);
      show.value = false;
    };

    const onDialogDismiss = () => {
      emit('dismiss');
    };

    return {
      NEUTRAL_SUPERDARK,
      show,
      handleBackdropClick,
      onDialogOk,
      onDialogCancel,
    };
  },
};
</script>
